/**
 * Legend widget — spec §F5.6 ("bottom-left default; draggable; auto-show/hide").
 *
 * §F5.6 lists five ordered blocks: (a) bivariate, (b) base indicator, (c) per-overlay,
 * (d) thematic layer, (e) analysis result layers. **M2 shipped (b); M3 added (d), (c) and
 * (a); M4 adds (e)**, the block the §F6 analysis dock registers its results in. They render
 * in the spec's order, and the widget appears whenever *any* of them is present.
 *
 * Block (a) does not sit *above* block (b): §F5.6 writes them as "(a) … **OR** (b) …", and
 * §F5.4 says bivariate mode replaces the single-indicator fill rather than adding to it. So
 * a bivariate map shows (a) where (b) was — one block per painted layer, always — and its
 * ✕ leaves bivariate mode, which brings (b) straight back.
 *
 * Block (b) is the mockup's `renderLegend()` markup, one-for-one: `h4` with the domain dot
 * + `name (unit) · year` + ⚙ + ✕, the `.lg-mode` Values | Low – High switch, k `.lg-row`
 * swatch rows, the ⚙-toggled `.style-panel` (AUTO + 9 ramp chips, method, classes,
 * opacity) and the `.tbl-note` footer. Its swatch ranges come from
 * `useChoroplethClasses()` — the same call §F5.3 paints with, so the legend can never
 * describe breaks the map is not using.
 *
 * Block (c) is one entry per overlay, reading its numbers and its §G4 colour from
 * `useOverlayClasses()` — the same call §F5.5 draws the markers with. Its ✕ calls the same
 * `removeOverlay` the sidebar's `+` does (§F5.11: "overlay + button ↔ legend block in
 * sync"), and its recolour swatches write `overlays[i].color`, which §G4 treats as the
 * override that beats the auto pick.
 *
 * Block (d) is one entry per switched-on thematic layer, in the catalogue's own order:
 * a ramp strip for a continuous raster, the `legend` class list for a categorical one, a
 * dot for points, a rule for lines. Its ✕ calls the *same* `toggleLayer` the §F5.2
 * checkbox does, so the two controls cannot disagree — there is no second source of truth
 * to keep in sync.
 *
 * Block (e) is §F5.6's `"Analysis result layers (n)" [desc line, ✕ clears all result
 * layers]` — one description line per registered result with a ✕ of its own, and a
 * block-level ✕ that clears the list. It reads the store's `results` and writes it through
 * the same two actions the §F6 dock's "Clear result layers (n)" uses, so the dock and the
 * legend are one control in two places. A result whose org_units are outside the geography
 * now on screen is drawn **dimmed with a note** rather than removed — see
 * `features/map/resultUnits.ts` for why that is a dimming and not a deletion.
 */
import { useMemo, useState } from 'react';
import { isCompactViewport } from '../../lib/viewport';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Seg } from '../../components/Seg';
import { Widget } from '../../components/Widget';
import {
  fetchConfig,
  fetchLayers,
  queryKeys,
  type Layer,
  type MetaConfig,
} from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import {
  levelLabelKey,
  useAppStore,
  type AppState,
  type ResultLayer,
} from '../../lib/state';
import {
  BIV_LABELS,
  bivColor,
  legendLabels,
  OV_POOL,
  parseRescale,
  symbolRadius,
  type LegendMode,
} from '../../lib/symbology';
import type { TFunction } from 'i18next';
import { useBivariateClasses, type BivClasses } from '../map/bivClasses';
import { useChoroplethClasses, type ChoroClasses } from '../map/choroClasses';
import { useOverlayClasses, type OverlayClasses } from '../map/overlayClasses';
import { drawnEpochTitle } from '../map/epochs';
import { useOutOfViewResults } from '../map/resultUnits';
import { provenanceKey } from './provenance';

/**
 * The nine ramps §F5.6 offers, in the mockup's chip order. `lc` (the 7-colour categorical
 * land-cover ramp in `/meta/config.ramps`) is deliberately absent: it is a thematic-layer
 * palette, not a sequential fill ramp for a continuous indicator.
 */
export const RAMP_KEYS = [
  'green',
  'brown',
  'blue',
  'ochre',
  'plum',
  'slate',
  'div',
  'divg',
  'ndvi',
] as const;

/** §F5.6 "Classes select (3–7)". */
export const CLASS_COUNTS = [3, 4, 5, 6, 7] as const;

export function Legend() {
  const { t } = useTranslation();
  const legendMode = useAppStore((s) => s.legendMode);
  const setLegendMode = useAppStore((s) => s.setLegendMode);
  const indStyle = useAppStore((s) => s.indStyle);
  const setIndStyle = useAppStore((s) => s.setIndStyle);
  const setIndicator = useAppStore((s) => s.setIndicator);
  const role = useAppStore((s) => s.role);
  const layersOn = useAppStore((s) => s.layersOn);
  const toggleLayer = useAppStore((s) => s.toggleLayer);
  const removeOverlay = useAppStore((s) => s.removeOverlay);
  const setOverlayColor = useAppStore((s) => s.setOverlayColor);
  const setMapMode = useAppStore((s) => s.setMapMode);
  const results = useAppStore((s) => s.results);
  const removeResultLayer = useAppStore((s) => s.removeResultLayer);
  const clearResultLayers = useAppStore((s) => s.clearResultLayers);
  const outOfView = useOutOfViewResults();
  const [styleOpen, setStyleOpen] = useState(false);
  const cls = useChoroplethClasses();
  // Non-null only once §F5.4 has both variables — which is exactly when block (a) replaces
  // block (b). While the reader is still picking Y the single fill is still on the map, and
  // so is the legend that describes it.
  const biv = useBivariateClasses();
  const overlays = useOverlayClasses();

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  // Not `enabled: layersOn.size > 0` — the §F5.2 pane has the same query in flight from the
  // moment the sidebar mounts, so this is a cache read, and gating it would only delay the
  // first block by a round trip after a permalink restores `layersOn`.
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });

  const onLayers = useMemo(
    () => (layers ?? []).filter((l) => layersOn.has(l.id)),
    [layers, layersOn],
  );

  // §F5.6 auto-show/hide: no blocks, no widget. Block (e) counts as a block — a reader with
  // an analysis on the map and nothing else must still get the legend that manages it.
  if (!cls && overlays.length === 0 && onLayers.length === 0 && results.length === 0) return null;

  return (
    <Widget
      id="mapLegend"
      title={t('legend.title')}
      icon="legend"
      defaultCollapsed={isCompactViewport()}
    >
      <div id="legendContent">
        {/* `biv.ready`, not just `biv`: the block must describe what is **painted**, and
            `Choropleth` keeps the single fill on screen until the Y payload lands. Showing
            the 3×3 grid a fetch early would put a nine-colour key over a green ramp. */}
        {biv && biv.ready ? (
          <BivariateBlock biv={biv} onExit={() => setMapMode('single')} />
        ) : cls ? (
          <BaseIndicatorBlock
            cls={cls}
            legendMode={legendMode}
            onLegendMode={setLegendMode}
            indStyle={indStyle}
            onIndStyle={setIndStyle}
            styleOpen={styleOpen}
            onToggleStyle={() => setStyleOpen((o) => !o)}
            onClear={() => setIndicator(null)}
          />
        ) : null}
        {overlays.map((ov) => (
          <OverlayBlock
            key={ov.id}
            ov={ov}
            onOff={() => removeOverlay(ov.id)}
            // §F5.6(c): clicking the swatch that is already selected clears the override
            // and hands the overlay back to §G4 — otherwise there is no way out of a manual
            // colour short of removing the overlay and adding it again.
            onColor={(color) =>
              setOverlayColor(ov.id, ov.overridden && ov.color === color ? undefined : color)
            }
          />
        ))}
        {onLayers.map((layer) => (
          <LayerBlock
            key={layer.id}
            layer={layer}
            ramps={config?.ramps ?? {}}
            onOff={() => toggleLayer(layer.id, false)}
            allLayers={layers}
          />
        ))}
        {results.length ? (
          <ResultBlock
            results={results}
            outOfView={outOfView}
            onRemove={removeResultLayer}
            onClear={clearResultLayers}
          />
        ) : null}
      </div>
    </Widget>
  );
}

interface ResultBlockProps {
  results: ResultLayer[];
  /** Ids whose org_units are outside the geography on screen (`useOutOfViewResults`). */
  outOfView: Set<string>;
  onRemove: (id: string) => void;
  onClear: () => void;
}

/**
 * §F5.6 block (e) — `"Analysis result layers (n)" [desc line, ✕ clears all result layers]`.
 *
 * **Two ✕s, and the spec names only one of them.** §F5.6 asks for the block-level clear;
 * this adds a ✕ per row, because every other block in this widget can be dismissed one item
 * at a time and a reader with four results who wants three of them would otherwise have to
 * clear all four and re-run three. The block ✕ is where §F5.6 puts it, in the header, and
 * does exactly what the §F6 dock's "Clear result layers (n)" does — the same store action,
 * so the two controls cannot disagree.
 *
 * **An `image` result prints its own key.** §H5's change overlay is a PNG whose colours no
 * client can re-derive — there are no per-unit values to classify — so the server sends the
 * swatches with the spec and they are drawn here, under the description line. Keyed on the
 * *kind* rather than on "does this spec carry a legend", because the heading is §H5's
 * sentence ("Changed to"): a future kind that arrives with swatches of its own would
 * otherwise be captioned with a claim about land cover.
 *
 * **The dimmed row is the honest answer to a geography change.** A result is painted from
 * the units the explorer is currently drawing (`features/map/resultUnits.ts`), so changing
 * country or level can take one off the map. Removing it from the list would be deleting an
 * analysis the reader waited for because they moved the map; saying it is out of view is
 * true, recoverable, and leaves the ✕ where they expect it.
 */
export function ResultBlock({ results, outOfView, onRemove, onClear }: ResultBlockProps) {
  const { t } = useTranslation();

  return (
    <div className="legend-block" data-block="results">
      <h4>
        <span style={{ flex: 1 }}>{t('legend.results', { n: results.length })}</span>
        <button
          type="button"
          className="lg-off"
          data-resclear=""
          aria-label={t('legend.resultsClear')}
          title={t('legend.resultsClear')}
          onClick={onClear}
        >
          <Icon name="x" size={12} />
        </button>
      </h4>

      {results.map((result) => {
        const dimmed = outOfView.has(result.id);
        return (
          <div
            className={`lg-row lg-res${dimmed ? ' dim' : ''}`}
            key={result.id}
            data-result={result.id}
            data-dimmed={dimmed ? '' : undefined}
          >
            <span className="lg-res-dot" data-restool={result.tool} aria-hidden="true" />
            <span className="lg-res-desc">
              {result.label}
              {dimmed ? (
                <span className="lg-res-note"> · {t('legend.resultOutOfView')}</span>
              ) : null}
              {result.spec.kind === 'image' && result.spec.legend?.length ? (
                <span className="lg-res-key">
                  <span className="lg-res-keyhead">{t('legend.changedTo')}</span>
                  {result.spec.legend.map((entry) => (
                    <span className="lg-res-keyrow" key={entry.value} data-reslegend={entry.value}>
                      <span className="lg-res-sw" style={{ background: entry.color }} />
                      {entry.label}
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              className="lg-off"
              data-resoff={result.id}
              aria-label={t('legend.resultRemove', { label: result.label })}
              title={t('legend.resultRemove', { label: result.label })}
              onClick={() => onRemove(result.id)}
            >
              <Icon name="x" size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

interface BivariateBlockProps {
  biv: BivClasses;
  onExit: () => void;
}

/**
 * §F5.6 block (a) — "BIVARIATE badge, `X × Y` names, 3×3 grid 24px cells with per-cell
 * tooltips, rotated Y label + X label, reading key, ✕".
 *
 * **The grid is drawn rows y=2 → y=0** (§G5: "Legend grid rendered rows y=2→0, high Y on
 * top"), which is the orientation of the axes it prints: Y climbs up the rotated label on
 * the left, X runs right along the label underneath. Reading the grid the way it is written
 * on screen therefore matches reading the map.
 *
 * The nine colours come from `biv.matrix` — `/meta/config.biv`, the same array the fill was
 * painted from — so the key cannot describe a palette the map is not using.
 *
 * **The grid is drawn only while `hasData`.** A scope where no unit reports *both*
 * indicators paints entirely no-data grey (see `Choropleth`), and a full nine-colour key
 * over that map is a lie about coverage — the same reasoning as block (b)'s "No values
 * reported" row (NOTES #187). The header and the ✕ stay, because the reader still needs to
 * know which pair produced the empty map and how to get out of it.
 */
export function BivariateBlock({ biv, onExit }: BivariateBlockProps) {
  const { t } = useTranslation();
  // The mockup's two shorteners: 26 characters for the header pair (the widget is 300px
  // wide and two full indicator names do not fit), two words for the axis labels.
  const short = (n: string) => (n.length > 26 ? `${n.slice(0, 24)}…` : n);
  const axis = (n: string) => n.split(' ').slice(0, 2).join(' ');

  return (
    <div className="legend-block" data-block="biv">
      <h4>
        <span className="biv-badge">{t('legend.bivBadge')}</span>
        <span style={{ flex: 1, fontSize: 11.5 }}>
          {short(biv.x.name)} × {short(biv.y.name)}
        </span>
        <button
          type="button"
          className="lg-off"
          data-bivoff=""
          aria-label={t('legend.bivExit')}
          title={t('legend.bivExit')}
          onClick={onExit}
        >
          <Icon name="x" size={12} />
        </button>
      </h4>

      {biv.hasData ? (
        <>
          <div className="biv-wrap">
            <div className="biv-ylab">{axis(biv.y.name)} ↑</div>
            <div>
              <div className="biv-grid" data-bivgrid="">
                {[2, 1, 0].map((yc) =>
                  [0, 1, 2].map((xc) => (
                    <span
                      key={`${yc}-${xc}`}
                      className="biv-cell"
                      data-bivcell={`${xc}-${yc}`}
                      style={{ background: bivColor(xc, yc, biv.matrix) }}
                      title={t('legend.bivCell', {
                        x: biv.x.name,
                        xClass: BIV_LABELS[xc],
                        y: biv.y.name,
                        yClass: BIV_LABELS[yc],
                      })}
                    />
                  )),
                )}
              </div>
              <div className="biv-xlab">{axis(biv.x.name)} →</div>
            </div>
            <div className="biv-key">{t('legend.bivKey')}</div>
          </div>

          <div className="tbl-note" data-bivfoot="">
            {t('legend.bivFoot', { year: biv.year })}
          </div>
        </>
      ) : (
        <div className="lg-row" data-nodata="">
          <span className="lg-swatch" style={{ background: '#EEF1EC', opacity: 0.5 }} />
          {t('legend.bivNoData', { year: biv.year })}
        </div>
      )}
    </div>
  );
}

interface OverlayBlockProps {
  ov: OverlayClasses;
  onOff: () => void;
  onColor: (color: string) => void;
}

/**
 * §F5.6 block (c) — one overlay indicator: "glyph (filled dot / ring), name, 3-size circle
 * key with min/mid/max values, 'recolour:' 4 pool swatches (selected ringed), ✕".
 *
 * **The key's circles are drawn at the level-1 scale (5 + √t·15) whatever the reporting
 * level is**, exactly as the mockup does. That is a deliberate departure from the markers:
 * at country level §F5.5 sizes them `10 + √t·26`, and a 72 px key circle would be wider
 * than the 300 px legend it sits in. The key is a *ranking* device — smallest / middle /
 * largest — and it still reads as one; the numbers beside it are the honest part and they
 * are the displayed set's real min, midpoint and max. Logged in NOTES.
 */
export function OverlayBlock({ ov, onOff, onColor }: OverlayBlockProps) {
  const { t } = useTranslation();
  const glyphLabel = t(ov.index === 0 ? 'legend.overlayCircles' : 'legend.overlayRings');

  return (
    <div className="legend-block" data-block="overlay" data-overlay={ov.id}>
      <h4>
        <span
          className="ov-glyph"
          aria-hidden="true"
          style={
            ov.index === 0
              ? { background: ov.color, border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.2)' }
              : { border: `3px solid ${ov.color}` }
          }
        />
        <span style={{ flex: 1 }}>
          {ov.name}
          {ov.unitLabel ? ` (${ov.unitLabel})` : ''}
        </span>
        <button
          type="button"
          className="lg-off"
          data-ovoff={ov.id}
          aria-label={t('legend.overlayRemove', { name: ov.name })}
          title={t('legend.overlayRemove', { name: ov.name })}
          onClick={onOff}
        >
          <Icon name="x" size={12} />
        </button>
      </h4>

      <div className="lg-size-row">
        {[0, 0.5, 1].map((f) => {
          const r = symbolRadius(f, 1);
          return (
            <span className="szc" key={f} data-ovsize={f}>
              <span
                style={{
                  width: r * 2,
                  height: r * 2,
                  borderRadius: '50%',
                  ...(ov.index === 0
                    ? { background: `${ov.color}cc`, border: '1.5px solid #fff' }
                    : { border: `3px solid ${ov.color}` }),
                }}
              />
              {fmtNum(ov.min + f * (ov.max - ov.min), ov.fmt)}
            </span>
          );
        })}
        <span style={{ fontSize: 10, color: 'var(--ink-3)', alignSelf: 'center' }}>
          {glyphLabel}
        </span>
      </div>

      <div className="lg-colorpick">
        {t('legend.recolour')}
        {OV_POOL.map((c) => (
          <i
            key={c}
            role="button"
            tabIndex={0}
            data-ovcol={c}
            // `ov.color` is the colour in force, override or auto — so the auto pick is
            // ringed too, and the swatches always show the reader where they are.
            className={ov.color.toLowerCase() === c.toLowerCase() ? 'sel' : undefined}
            style={{ background: c }}
            aria-label={t('legend.recolourTo', { color: c })}
            title={c}
            onClick={() => onColor(c)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onColor(c);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface LayerBlockProps {
  layer: Layer;
  ramps: MetaConfig['ramps'];
  onOff: () => void;
  /** Every catalogue layer — so a drawn epoch member can find its series. See `title` below. */
  allLayers: readonly Layer[] | undefined;
}

/**
 * §F5.6 block (d) — one switched-on thematic layer.
 *
 * The ramp strip is drawn from `/meta/config.ramps[layer.ramp]`, the §G palette, **not**
 * from the matplotlib colormap TiTiler actually renders with: `colormap_name` is a
 * translation of the ramp key (Task 1, NOTES #231), so the strip is an approximation of
 * the pixels by construction. It is the honest one available today — the alternative is a
 * legend keyed to a palette the design never chose — and Task 1/2 already flag replacing
 * `colormap_name` with an explicit `colormap=` built from these very stops as the fix.
 */
export function LayerBlock({ layer, ramps, onOff, allLayers }: LayerBlockProps) {
  const { t } = useTranslation();
  const stops = layer.ramp ? (ramps[layer.ramp] ?? []) : [];
  const rescale = parseRescale(layer.tile_template);
  const categorical = layer.is_categorical && (layer.legend?.length ?? 0) > 0;
  // Client, 2026-09-15: "a general name - Land cover, then the year can show when a user
  // filters by year" — `lca2019`'s own name is a real title ("Land cover 2019 — annual
  // 10 m …"), but this block is titled by what is *drawn*, and a drawn epoch reads
  // "series name · year" instead (`epochs.drawnEpochTitle`).
  const title = drawnEpochTitle(allLayers, layer.id, layer.name);

  return (
    <div className="legend-block" data-block="layer" data-layer={layer.id}>
      <h4>
        <span style={{ flex: 1 }}>{title}</span>
        <button
          type="button"
          className="lg-off"
          data-off={layer.id}
          aria-label={t('layers.turnOff', { name: title })}
          title={t('layers.turnOff', { name: title })}
          onClick={onOff}
        >
          <Icon name="x" size={12} />
        </button>
      </h4>

      {categorical ? (
        (layer.legend ?? []).map((entry) => (
          <div className="lg-row" key={entry.label} data-lgclass={entry.label}>
            <span className="lg-swatch" style={{ background: entry.color }} />
            {entry.label}
          </div>
        ))
      ) : layer.kind === 'points_api' ? (
        <div className="lg-row">
          <span className="lg-pt" data-lgpt="" style={{ background: 'var(--brand)' }} />
          {t('layers.legendPoints')}
        </div>
      ) : layer.kind === 'lines_api' ? (
        <div className="lg-row">
          <span className="lg-line" data-lgline="" style={{ borderColor: 'var(--brand)' }} />
          {t('layers.legendLines')}
        </div>
      ) : layer.kind === 'polygons_api' ? (
        // Wave 4's third vector geometry: HydroLAKES shorelines and the GRID3 settlement
        // extents. A filled swatch rather than the point dot or the line rule, because what
        // the reader is being told is "this layer paints areas".
        <div className="lg-row">
          <span
            className="lg-swatch"
            data-lgarea=""
            style={{ background: 'var(--brand)', opacity: 0.55, borderRadius: 2 }}
          />
          {t('layers.legendAreas')}
        </div>
      ) : stops.length ? (
        <>
          <div className="lg-row lg-ramp" data-lgramp={layer.ramp}>
            {stops.map((color, i) => (
              <span
                key={`${color}-${i}`}
                className="lg-swatch"
                style={{ background: color, width: 26, borderRadius: 0 }}
              />
            ))}
          </div>
          <div className="lg-row lg-ends">
            <span>{rescale ? rescale[0] : t('layers.legendLow')}</span>
            <span>{rescale ? rescale[1] : t('layers.legendHigh')}</span>
          </div>
        </>
      ) : (
        // A raster with no §G ramp key (or a ramp the config does not carry) still gets a
        // block: the reader needs to know *which* layer is painting, and "no ramp" is a
        // catalogue gap worth showing rather than a reason to render nothing.
        <div className="lg-row" data-lgnoramp="">
          {t('layers.legendNoRamp')}
        </div>
      )}
    </div>
  );
}

interface BlockProps {
  cls: ChoroClasses;
  legendMode: LegendMode;
  onLegendMode: (mode: LegendMode) => void;
  indStyle: AppState['indStyle'];
  onIndStyle: (patch: Partial<AppState['indStyle']>) => void;
  styleOpen: boolean;
  onToggleStyle: () => void;
  onClear: () => void;
}

/* ------------------------------------------------------ §G3 classes-mode labels */

/**
 * §G3 "Classes mode by k", **translated** — the one legend string `symbology.ts` cannot own.
 *
 * `symbology.classLabels` hard-codes `['Low','Medium','High']` and must keep doing so: it is
 * pinned character-for-character against `apps/analysis/symbology.py` by the committed golden
 * table (`symbology.cases.json`, NOTES #559), and the parity check is what guarantees a
 * choropleth painted in the browser and a legend baked into an exported PNG agree.
 *
 * But the Python side takes a `language` and the port's caller (`legend.py`) passes it, so a
 * French reader exporting a map in classes mode got "Faible / Moyen / Élevé" in the file and
 * "Low / Medium / High" in the widget beside it. The two are supposed to be the same legend.
 *
 * So the *labels* move through `t()` and the *shape* stays in `symbology.ts`: same three
 * named tables for k = 3/4/5, same generated form (`Lowest`, `Class 2` … `Highest`) for
 * everything else, so a k the tables do not name reads identically in both places. The keys
 * mirror `apps.analysis.symbology._CLASS_LABELS` entry for entry.
 *
 * Not exported: the only consumer is the block below, and `Legend.test.tsx` asserts on the
 * rendered rows rather than on this — which is the assertion that would actually have caught
 * the divergence.
 */
function classLabels(k: number, t: TFunction): string[] {
  if (k === 3) return [t('legend.classLow'), t('legend.classMedium'), t('legend.classHigh')];
  if (k === 4) {
    return [
      t('legend.classLow'),
      t('legend.classMediumLow'),
      t('legend.classMediumHigh'),
      t('legend.classHigh'),
    ];
  }
  if (k === 5) {
    return [
      t('legend.classVeryLow'),
      t('legend.classLow'),
      t('legend.classMedium'),
      t('legend.classHigh'),
      t('legend.classVeryHigh'),
    ];
  }
  return Array.from({ length: k }, (_, i) =>
    i === 0
      ? t('legend.classLowest')
      : i === k - 1
        ? t('legend.classHighest')
        : t('legend.classN', { n: i + 1 }),
  );
}

/** §F5.6 block (b) — the base (fill) indicator. */
function BaseIndicatorBlock({
  cls,
  legendMode,
  onLegendMode,
  indStyle,
  onIndStyle,
  styleOpen,
  onToggleStyle,
  onClear,
}: BlockProps) {
  const { t } = useTranslation();
  const name = cls.meta?.name ?? cls.indicatorId;
  const labels = useMemo(
    () =>
      legendMode === 'classes'
        ? classLabels(indStyle.k, t)
        : legendLabels(cls.breaks, cls.fmt, legendMode, indStyle.k),
    [cls.breaks, cls.fmt, legendMode, indStyle.k, t],
  );
  const ramps = cls.config?.ramps ?? {};
  const pct = Math.round(indStyle.opacity * 100);

  return (
    <div className="legend-block" data-block="base">
      <h4>
        <span
          className="dot"
          aria-hidden="true"
          style={{
            width: 9,
            height: 9,
            borderRadius: 3,
            background: cls.domain?.color ?? 'var(--ink-3)',
            flex: 'none',
          }}
        />
        <span style={{ flex: 1 }}>
          {name}
          {cls.unitLabel ? ` (${cls.unitLabel})` : ''} ·{' '}
          {/* The header dates the numbers. In the cumulative reading "2026" alone would be
              a misdating — the figure is everything up to 2026 — so the header says which
              of the two it is, and `cls.mode` is the *payload's* mode, not the store's, so
              it can never run ahead of the paint. */}
          {cls.mode === 'cumulative' ? t('timebar.toYear', { year: cls.year }) : cls.year}
        </span>
        <button
          type="button"
          className="lg-off"
          data-stylebtn=""
          aria-pressed={styleOpen}
          aria-label={t('legend.style')}
          title={t('legend.styleTitle')}
          onClick={onToggleStyle}
        >
          ⚙
        </button>
        <button
          type="button"
          className="lg-off"
          data-indoff=""
          aria-label={t('legend.clear')}
          title={t('legend.clear')}
          onClick={onClear}
        >
          <Icon name="x" size={12} />
        </button>
      </h4>

      <Seg
        className="lg-mode"
        ariaLabel={t('legend.mode')}
        value={legendMode}
        onChange={onLegendMode}
        options={[
          { value: 'values', label: t('legend.modeValues') },
          { value: 'classes', label: t('legend.modeClasses') },
        ]}
      />

      {/* Three states, in this order. `!ready` is the one that is easy to get wrong:
          "no values reported" is a *finding*, and asserting it while the payload is still
          in flight would print a falsehood on the one panel readers trust for coverage. */}
      {!cls.ready ? (
        <div className="lg-row" data-lgloading="">
          <span className="lg-swatch skeleton" />
          {t('legend.loading')}
        </div>
      ) : cls.hasData ? (
        cls.colors.map((color, i) => (
          <div className="lg-row" key={`${color}-${i}`} data-lgrow={i}>
            <span className="lg-swatch" style={{ background: color }} />
            {labels[i]}
          </div>
        ))
      ) : (
        <div className="lg-row" data-nodata="">
          <span
            className="lg-swatch"
            style={{ background: '#EEF1EC', opacity: 0.5 }}
          />
          {/* Two ways to have nothing, and they are not the same finding. For a roll-up
              indicator an empty level means the *year* was not reported — §E4 would have
              derived a value from anywhere below or beside it if there had been one. For a
              survey estimate (`aggregation: 'none'`) the year is fine and the **level** is
              the answer: the MELS baseline was estimated at country and watershed, so a
              region map is empty by design and "no values reported for 2025" would send a
              reader looking for a reporting failure that is not there. */}
          {cls.meta?.aggregation === 'none'
            ? t('legend.noDataAtLevel')
            : t('legend.noData', { year: cls.year })}
        </div>
      )}

      {styleOpen ? (
        <div className="style-panel" data-stylepanel="">
          <div className="sb-title" style={{ marginBottom: 5 }}>
            {t('legend.layerStyle')}
          </div>
          <div className="ramp-chips">
            <button
              type="button"
              className={`ramp-chip auto${indStyle.ramp === null ? ' sel' : ''}`}
              data-ramp=""
              aria-pressed={indStyle.ramp === null}
              title={t('legend.rampAuto')}
              onClick={() => onIndStyle({ ramp: null })}
            >
              AUTO
            </button>
            {RAMP_KEYS.map((key) => {
              const stops = ramps[key];
              return (
                <button
                  type="button"
                  key={key}
                  className={`ramp-chip${indStyle.ramp === key ? ' sel' : ''}`}
                  data-ramp={key}
                  aria-pressed={indStyle.ramp === key}
                  aria-label={key}
                  title={key}
                  style={
                    stops ? { background: `linear-gradient(90deg,${stops.join(',')})` } : undefined
                  }
                  onClick={() => onIndStyle({ ramp: key })}
                />
              );
            })}
          </div>

          <div className="style-row">
            <label htmlFor="stMethod">{t('legend.method')}</label>
            <select
              id="stMethod"
              data-stmethod=""
              value={indStyle.method}
              onChange={(e) =>
                onIndStyle({ method: e.target.value as AppState['indStyle']['method'] })
              }
            >
              <option value="quantile">{t('legend.methodQuantile')}</option>
              <option value="equal">{t('legend.methodEqual')}</option>
            </select>
            <label htmlFor="stK">{t('legend.classes')}</label>
            <select
              id="stK"
              data-stk=""
              value={indStyle.k}
              onChange={(e) => onIndStyle({ k: Number(e.target.value) })}
            >
              {CLASS_COUNTS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="style-row">
            <label htmlFor="stOpacity">{t('legend.opacity')}</label>
            <input
              id="stOpacity"
              type="range"
              min={0.2}
              max={1}
              step={0.02}
              value={indStyle.opacity}
              data-stop=""
              onChange={(e) => onIndStyle({ opacity: Number(e.target.value) })}
            />
            <span style={{ fontFamily: 'var(--mono)', width: 32, textAlign: 'right' }}>{pct}%</span>
          </div>
        </div>
      ) : null}

      {/* The swatch ranges then describe the whole programme rather than what is on screen,
          and a legend that does not say so is a legend claiming the filtered map spans them.
          See `classificationBasis` for why the classes are anchored at all. */}
      {cls.anchored ? (
        <div className="tbl-note" data-lganchored="">
          {t('legend.footAnchored')}
        </div>
      ) : null}

      <div className="tbl-note" data-lgfoot="">
        {t(indStyle.method === 'equal' ? 'legend.footEqual' : 'legend.footQuantile')} ·{' '}
        {t('legend.footClasses', { k: indStyle.k })} ·{' '}
        {t('legend.footLevel', { level: t(levelLabelKey(cls.level)) })} ·{' '}
        {cls.mode === 'cumulative' ? (
          <>
            <span data-lgcum="">{t('legend.footCumulative', { year: cls.year })}</span> ·{' '}
          </>
        ) : null}
        {t(provenanceKey(cls.meta?.sources))}
      </div>
    </div>
  );
}

export default Legend;
